/**
 * Part 19 — In-product NPS prompt modal.
 *
 * Behavior:
 *   - On mount, polls /nps/eligibility once.
 *   - If eligible, renders a centered modal with 0-10 scale + optional comment.
 *   - Submitting POSTs to /nps/responses. Closing without submitting POSTs
 *     to /nps/dismiss so the user isn't re-prompted for 7 days.
 *   - For promoters (9-10), the post-submit thank-you surfaces a review CTA
 *     (G2 / Capterra) — opt-in social proof play.
 *
 * Mount this once near the app root (e.g. inside the authenticated layout).
 */
import { useEffect, useState } from 'react'

import { npsService, type NpsPromptReason } from '../../services/nps'

interface Props {
  /** Optional G2 review URL — when set, promoters see a one-click CTA. */
  g2ReviewUrl?: string
  /** Optional Capterra review URL. */
  capterraReviewUrl?: string
}

const REASON_TITLES: Record<NpsPromptReason, string> = {
  trial_day_7: 'How are things going so far?',
  trial_day_30: 'A month in — how would you rate Karyva?',
  quarterly: 'Quick check-in: how likely are you to recommend Karyva?',
  manual: 'How likely are you to recommend Karyva?',
}

export function NpsPrompt({ g2ReviewUrl, capterraReviewUrl }: Props) {
  const [reason, setReason] = useState<NpsPromptReason | null>(null)
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    npsService
      .eligibility()
      .then((res) => {
        if (cancelled) return
        if (res.eligible && res.prompt_reason) setReason(res.prompt_reason)
      })
      .catch(() => {
        /* eligibility failure is non-fatal — silently skip */
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (!reason) return null

  const handleSubmit = async () => {
    if (score == null) return
    setSubmitting(true)
    try {
      await npsService.submit({
        score,
        comment: comment.trim() || null,
        prompt_reason: reason,
      })
      setSubmitted(true)
    } catch {
      // surface a minimal inline error; don't crash the app.
      setSubmitting(false)
      return
    }
    setSubmitting(false)
  }

  const handleDismiss = async () => {
    try {
      await npsService.dismiss(reason)
    } catch {
      /* dismissal failure is silent */
    }
    setReason(null)
  }

  const isPromoter = score != null && score >= 9
  const hasReviewLinks = !!g2ReviewUrl || !!capterraReviewUrl

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        {!submitted ? (
          <>
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#102a43]">
                {REASON_TITLES[reason]}
              </h2>
              <button
                onClick={handleDismiss}
                aria-label="Dismiss"
                className="text-[#829ab1] hover:text-[#102a43] text-xl leading-none"
              >
                ×
              </button>
            </div>

            <p className="text-sm text-[#486581] mb-4">
              On a scale from 0 to 10, how likely are you to recommend Karyva to
              a colleague?
            </p>

            <div className="grid grid-cols-11 gap-1 mb-2">
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  onClick={() => setScore(n)}
                  className={`py-2 rounded text-sm border transition-colors ${
                    score === n
                      ? 'bg-[#102a43] text-white border-[#102a43]'
                      : 'bg-white text-[#486581] border-[#bcccdc] hover:border-[#486581]'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-[#829ab1] mb-4">
              <span>Not at all likely</span>
              <span>Extremely likely</span>
            </div>

            <label className="block">
              <span className="text-sm text-[#486581] mb-2 block">
                What's the main reason for your score? (optional)
              </span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={4000}
                className="w-full px-3 py-2 border border-[#bcccdc] rounded text-sm focus:outline-none focus:border-[#486581]"
              />
            </label>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={handleDismiss}
                disabled={submitting}
                className="px-4 py-2 text-sm text-[#486581] hover:text-[#102a43]"
              >
                Not now
              </button>
              <button
                onClick={handleSubmit}
                disabled={score == null || submitting}
                className="px-4 py-2 bg-[#102a43] text-white text-sm rounded hover:bg-[#243b53] disabled:opacity-50"
              >
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <h2 className="text-lg font-semibold text-[#102a43] mb-2">
              Thank you!
            </h2>
            <p className="text-sm text-[#486581] mb-6">
              We read every response. It directly shapes what we build next.
            </p>

            {isPromoter && hasReviewLinks && (
              <div className="bg-[#f0f4f8] rounded p-4 mb-4 text-left">
                <p className="text-sm text-[#102a43] mb-3 font-medium">
                  Mind sharing that on a review site? It takes about 2 minutes
                  and goes a long way for a young product.
                </p>
                <div className="flex gap-2 flex-wrap">
                  {g2ReviewUrl && (
                    <a
                      href={g2ReviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-white border border-[#bcccdc] rounded text-sm text-[#102a43] hover:border-[#486581]"
                    >
                      Review on G2
                    </a>
                  )}
                  {capterraReviewUrl && (
                    <a
                      href={capterraReviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-white border border-[#bcccdc] rounded text-sm text-[#102a43] hover:border-[#486581]"
                    >
                      Review on Capterra
                    </a>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setReason(null)}
              className="px-4 py-2 bg-[#102a43] text-white text-sm rounded hover:bg-[#243b53]"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
